import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { getSlackChannels } from '@atlassian/search-client/slack-channels';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { fuzzyFilter } from '../../../../../common/utils/filters/fuzzy-match';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const products3PFromConfig = getProducts3PFromConfigForFilter('slackChannel');

// 200 recommended by Slack as the max per request
// Select Filter does not support pagination, but the backend API does via cursor
// This filter can be enhanced in the future to support pagination on scroll/search if needed
const MAX_CHANNELS_TO_FETCH = 200;

/**
 * Session-level cache: maps cloudId → in-flight or resolved promise of channel options.
 * This deduplicates concurrent and repeated calls (e.g. queryParams.fetcher + initialOptions.fetcher
 * both fire on page load when a channel is pre-selected via URL param).
 * The cache is module-level so it persists for the browser session but resets on page navigation.
 */
const channelOptionsCache = new Map<string, Promise<SelectFilterOption[]>>();

/** Clears the channel options cache. Exported for use in tests only. */
export const clearChannelOptionsCache = (): void => channelOptionsCache.clear();

async function doFetchSlackChannels(config: CloudConfig): Promise<SelectFilterOption[]> {
	const { cloudId } = config;

	if (!cloudId) {
		throw new Error('Cloud ID is required to fetch Slack channels');
	}

	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});

	try {
		const payload = await getSlackChannels({
			cloudId,
			limit: MAX_CHANNELS_TO_FETCH,
			aggAbsoluteUrl: config.aggAbsoluteUrl,
			headers,
		});

		return (payload?.channels ?? []).map((channel) => ({
			value: channel.name,
			trackingKey: channel.id,
			queryParamValue: channel.name,
			label: channel.name,
			isDefault: false,
			isSelected: false,
			isSuggested: false,
			product: config.product,
		}));
	} catch {
		return [];
	}
}

const fetchSlackChannelOptions = (config: CloudConfig): Promise<SelectFilterOption[]> => {
	const { cloudId } = config;
	if (!cloudId) {
		return Promise.resolve([]);
	}
	// Return cached promise (handles both in-flight dedup and session-level caching).
	// If the fetch returns an empty result (e.g. network blip, auth error — doFetchSlackChannels
	// catches internally and returns []) or rejects unexpectedly, remove the cache entry so a
	// subsequent open/retry can attempt a fresh fetch rather than returning stale empty results.
	if (!channelOptionsCache.has(cloudId)) {
		const promise = doFetchSlackChannels(config);
		channelOptionsCache.set(cloudId, promise);
		promise
			.then((channels) => {
				if (channels.length === 0) {
					channelOptionsCache.delete(cloudId);
				}
			})
			.catch(() => {
				channelOptionsCache.delete(cloudId);
			});
	}
	return channelOptionsCache.get(cloudId)!;
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	// Hydrate selected channel names from URL params by client-side filtering
	// the full fetched list — no extra API call needed.
	const allOptions = await fetchSlackChannelOptions(config);
	const selected = new Set(queryParams);
	return allOptions.filter((option) => selected.has(option.queryParamValue));
};

const lookupChannels = async (
	query: string,
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchSlackChannelOptions(config);
	if (!query) {
		return allOptions;
	}
	return fuzzyFilter(query, allOptions, (option) => option.label);
};

export const filter: CreatedSelectFilter<'slackChannel'> = createMultiSelectFilter({
	id: 'slackChannel',
	products: products3PFromConfig,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'slackChannels',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchSlackChannelOptions(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return lookupChannels(query, config);
		},
	},
});
